var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Component, Vue, Watch } from 'vue-property-decorator';
import SelectFilter from '@/components/select-filter/SelectFilter';
import AreaSelect from '@/components/AreaSelect/AreaSelect';
import TopicSelect from '@/components/TopicSelect/TopicSelect';
import { SubjectStatusSummary } from '@/models/SubjectStatusSummary';
import EditSubjectDialog from '@/components/curriculum/EditSubjectDialog';
import StudentSubjectStatusSummary from '@/components/StudentSubjectStatusSummary/StudentSubjectStatusSummary';
import { debounce, uniq } from 'lodash';
import { UserLevel } from '@/models/UserLevel';
import { UserPeriod } from '@/models/UserPeriod';
import { Year } from '@/models/Year';
import ErrorHandler from '@/support/ErrorHandler';
import { RolesEnum } from '@/items/Roles';
import TreatmentDialog from '@/views/Curricula/TreatmentDialog';
import { generateUuid, hex2rgba } from '@/support/String';
import client from '@/support/Client';
import Storage from '@/support/Storage';
import { whiteKidIcons, whiteAreaIcons } from '@/support/Icon';
import ProgressSelect from '@/components/progress-select/ProgressSelect';
import { UserSubject, UserSubjectStatusEnum } from '@/models/UserSubject';
import TrafficLight from '@/components/TrafficLight/TrafficLight';
import { Template } from '@/models/Template';
import ScoolSnackbar from '@/components/ScoolSnackbar/ScoolSnackbar';
import { excludeRoles } from '@/support/ExcludeRoles';
import { UserSubject as UserSubjectModel } from '@/models/UserSubject';
import BulkUpdateDescriptionDialog from '@/components/dialog/BulkUpdateDescriptionDialog/BulkUpdateDescriptionDialog';
import ProgressDialog from '@/components/ProgressDialog/ProgressDialog.vue';
import UserSubjectMessageForm from '@/components/user-subject-message-form/UserSubjectMessageForm';
import GoalsetSelect from '@/components/GoalsetSelect/GoalsetSelect';
let StudentOverview = class StudentOverview extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #region @Props
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isLoading = true;
        this.students = [];
        this.tableItems = [];
        this.studentBeingLoaded = null;
        this.isLoadingAllStudents = false;
        this.years = [];
        this.tableFilters = {
            levels: {},
            periods: {},
            years: [],
            subject_labels: {},
            area: '',
            topic: '',
            student_profile_levels: [],
        };
        this.RolesEnum = RolesEnum;
        this.isEditingTreatment = false;
        this.selectedStudentId = '';
        this.debouncedSetTableItems = debounce(this.setTableItems, 500);
        this.isStudentSubjectLoading = false;
        this.isUpdatingStatus = false;
        this.student_can_set_goal_to_yellow = true;
        this.subjectTarget = null;
        this.showEditSubjectDialog = false;
        this.templates = [];
        this.currentUserDialog = null;
        this.previousUserSubjectStatus = '';
        this.selectedStudentSubjects = [];
        this.statusColorMap = {
            highlighted: '#f8c623',
            busy: '#008ac4',
            old: '#ff7800',
            inactive: '#9b9b9b',
            closed: '#d0021b',
            finished: '#00b900',
        };
        this.isDisplayingBulkUpdateDescriptionDialog = false;
        this.isDisplayingProgressDialog = false;
        this.isDisplayingIdpNotesDialog = false;
        this.isFilteredOnGoalset = false;
        this.group = {};
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.group = this.$attrs.group;
        this.isLoading = true;
        this.updatePageTitle();
        this.setTableItems();
        await this.getYears();
        this.isLoading = false;
    }
    // #endregion
    // #region Class methods
    toggleSelection(studentSubject) {
        const index = this.selectedStudentSubjects.indexOf(studentSubject);
        if (index !== -1) {
            this.selectedStudentSubjects.splice(index, 1);
        }
        else {
            this.selectedStudentSubjects.push(studentSubject);
        }
    }
    updatePageTitle() {
        this.$emit('updatePageTitle', this.$tc('student_overview'));
    }
    onClickNavigateToIdp(student) {
        const route = this.$router.resolve({
            name: 'students.show',
            params: { id: student.id || '' },
        });
        window.open(route.href, '_blank');
    }
    onClickOpenTreatmentDialog(userId) {
        this.selectedStudentId = userId;
        this.isEditingTreatment = true;
    }
    handleTreatmentSaved() {
        this.setTableItems();
        this.isEditingTreatment = false;
    }
    async onClickLoadAllStudentsInfo() {
        this.isLoadingAllStudents = true;
        const promises = this.tableItems.map((item) => this.onClickLoadStudentInfo(item));
        await Promise.all(promises);
        this.isLoadingAllStudents = false;
    }
    async onClickLoadStudentInfo(summary) {
        const foundItem = this.tableItems
            .find((item) => item.user.id === summary.user.id);
        if (!(foundItem && foundItem.user && foundItem.user.id)) {
            return;
        }
        this.studentBeingLoaded = this.isLoadingAllStudents ? null : foundItem.user.id;
        const filter = {
            subject_id: this.tableFilters.topic,
        };
        const [userLevel, userPeriod] = await Promise.all([
            this.fetchUserLevel(foundItem.user.id, filter),
            this.fetchUserPeriod(foundItem.user.id, filter),
        ]).catch(() => {
            return [null, null];
        });
        const user = foundItem.user;
        if (userLevel) {
            user.level = (userLevel.level && userLevel.level.name) || '';
        }
        if (userPeriod) {
            user.period = (userPeriod.period && userPeriod.period.name) || '';
        }
        this.studentBeingLoaded = null;
    }
    hasLevelAndPeriod(user) {
        if (!user) {
            return false;
        }
        return !!(user && user.level && user.period);
    }
    sortItems(items, key = 'name') {
        return items.sort((a, b) => {
            const nameA = a[key].match(/\d+(\.\d+)?/g);
            const nameB = b[key].match(/\d+(\.\d+)?/g);
            if (nameA && nameB) {
                for (let i = 0; i < Math.min(nameA.length, nameB.length); i++) {
                    const numA = parseFloat(nameA[i]);
                    const numB = parseFloat(nameB[i]);
                    if (numA !== numB) {
                        return numA - numB;
                    }
                }
                return a[key].localeCompare(b[key]);
            }
            else {
                return a[key].localeCompare(b[key]);
            }
        });
    }
    createStudentSubjectsArray(subjects) {
        const uniqueAreas = new Map();
        subjects.forEach((subject) => {
            const areaId = subject.area.id;
            const topicId = subject.parent.id;
            if (!uniqueAreas.has(areaId)) {
                uniqueAreas.set(areaId, { area: subject.area, topics: new Map() });
            }
            if (!uniqueAreas.get(areaId).topics.has(topicId)) {
                uniqueAreas.get(areaId).topics.set(topicId, { topic: subject.parent, subjects: [] });
            }
            uniqueAreas.get(areaId).topics.get(topicId).subjects.push(subject);
        });
        const resultArray = Array.from(uniqueAreas.values()).map((areaData) => {
            const topicsArray = Array.from(areaData.topics.values()).map((topicData) => {
                return {
                    id: topicData.topic.id,
                    name: topicData.topic.name,
                    subjects: this.sortItems(topicData.subjects, 'sort_name'),
                };
            });
            return {
                color: areaData.area.color,
                icon: areaData.area.icon,
                id: areaData.area.id,
                name: areaData.area.name,
                topics: this.sortItems(topicsArray),
            };
        });
        return this.sortItems(resultArray);
    }
    setSettings() {
        if (this.$store.state.Auth.isStudent) {
            const studentCanEditGoalToYellow = this.$store.state.settings.find((setting) => setting.key === 'student_can_set_goal_to_yellow');
            if (studentCanEditGoalToYellow) {
                this.student_can_set_goal_to_yellow = studentCanEditGoalToYellow.value === '1' || studentCanEditGoalToYellow.value === true ? true : false;
            }
        }
    }
    isGreenTrafficLightVisible(subject) {
        return !!(this.canSeeGreenTraficLight && subject.status !== UserSubjectStatusEnum.FINISHED && subject.status !== UserSubjectStatusEnum.OLD);
    }
    isYellowTrafficLightVisible(subject) {
        return !!(((subject.status === 'busy' || subject.status === 'old')
            && (this.$store.state.Auth.hasRole([RolesEnum.TEACHER, RolesEnum.ADMIN, RolesEnum.RESELLER, RolesEnum.ROOT])
                || (this.$store.state.Auth.hasRole([RolesEnum.STUDENT]) && this.student_can_set_goal_to_yellow))));
    }
    emptySelected() {
        this.selectedStudentSubjects = [];
    }
    // #endregion
    // #region Async methods
    async expandClick(props) {
        props.expanded = !props.expanded;
        if (!props.item.student_subjects.length) {
            this.isStudentSubjectLoading = true;
            const response = await client('get', `groups/${props.item.user.id}/subjects`, { filters: this.studentFilters, });
            props.item.student_subjects = this.createStudentSubjectsArray(response.data);
            this.isStudentSubjectLoading = false;
        }
    }
    async handleTrafficLightChange(subject, status, topicId, event, user, subjectStatusSumary, oldStatus) {
        event.setLoadingStateOn();
        await this.updateStatus(subject, status, {}, topicId, user)
            .catch(() => {
            event.setLoadingStateOff();
        });
        event.setLoadingStateOff();
        this.tableItems = this.tableItems.map((item) => {
            if (subjectStatusSumary && item.user.id === subjectStatusSumary.user.id) {
                item.subjects[oldStatus] -= 1;
                item.subjects[status] += 1;
            }
            return item;
        });
    }
    async updateStatus(subject, status, meta = {}, topicId = null, user) {
        this.isUpdatingStatus = true;
        const userSubjects = await new UserSubject()
            .user(user)
            .put([
            {
                ...meta,
                ...{
                    subject: subject.subject,
                    status,
                },
            },
        ]);
        if (userSubjects) {
            subject.status = status;
            this.parseSubjectDescription(subject.id || '', topicId, userSubjects);
        }
        this.isUpdatingStatus = false;
    }
    async onBulkProgressChanged(progress) {
        const subjectsToBeMappedTo = [];
        const promises = this.selectedStudentSubjects.map(async (selectedStudentSubject) => {
            const mapObject = {
                subject: selectedStudentSubject.subject,
                user: selectedStudentSubject['user'],
                progress,
            };
            subjectsToBeMappedTo.push(mapObject);
            new UserSubjectModel().user(selectedStudentSubject['user'] || '').put([{ subject: selectedStudentSubject.subject, progress }]);
        });
        await Promise.all(promises);
        this.tableItems.forEach((item) => {
            item.student_subjects.forEach((studentSubject) => {
                studentSubject.topics.forEach((topic) => {
                    topic.subjects.forEach((subject) => {
                        const matchingChanges = subjectsToBeMappedTo.filter((change) => change.user === subject.user && change.subject === subject.subject);
                        matchingChanges.forEach((matchingChange) => {
                            subject.progress = matchingChange.progress;
                        });
                    });
                });
            });
        });
    }
    async onBulkStatusChanged(status, isDefault) {
        const subjects = [];
        const subjectsToBeMappedTo = [];
        this.selectedStudentSubjects.map((userSubject) => {
            const bulkUpdateSubject = {
                subject: userSubject.subject,
                user: userSubject.user,
            };
            subjects.push(bulkUpdateSubject);
            const objectToMap = {
                oldStatus: userSubject.status,
                newStatus: status,
                user: userSubject.user,
                subject: userSubject.subject,
            };
            subjectsToBeMappedTo.push(objectToMap);
        });
        const payload = {
            status,
            subjects,
        };
        await new UserSubjectModel().bulkUpdate(payload).catch((error) => {
            ErrorHandler.network(error);
        });
        this.tableItems.forEach((item) => {
            item.student_subjects.forEach((studentSubject) => {
                studentSubject.topics.forEach((topic) => {
                    topic.subjects.forEach((subject) => {
                        const matchingChanges = subjectsToBeMappedTo.filter((change) => change.user === subject.user && change.subject === subject.subject);
                        matchingChanges.forEach((matchingChange) => {
                            subject.status = matchingChange.newStatus;
                        });
                    });
                });
            });
            subjectsToBeMappedTo.forEach((matchingChange) => {
                if (item.user.id === matchingChange.user) {
                    item.subjects[matchingChange.oldStatus]--;
                    item.subjects[matchingChange.newStatus]++;
                }
            });
        });
    }
    parseSubjectDescription(subjectId, topicId, userSubjects) {
        if (!topicId) {
            return;
        }
        const userSubject = userSubjects.find((currentUserSubject) => currentUserSubject.id === subjectId);
        if (!userSubject) {
            return;
        }
    }
    showSubjectDialog(subject, ignoreStatusCheck = false, user) {
        if (['dismissed', 'future'].includes(subject.status || '') && !ignoreStatusCheck) {
            return;
        }
        this.subjectTarget = subject;
        this.showEditSubjectDialog = true;
        this.currentUserDialog = user;
        const subjectId = this.subjectTarget.subject || '';
        if (!this.$store.state.Auth.hasRole([RolesEnum.GUARDIAN, RolesEnum.STUDENT])) {
            this.getTemplates(subjectId);
        }
    }
    get dialogOptionsResetStudentStatuses() {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: this.$t('dialogOptions.reset', { item: this.$t('dialogOptions.item.helpAndFinished') }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.reset').toString(),
                    action: () => {
                        const subjects = [];
                        const users = [];
                        this.selectedStudentSubjects.forEach((selectedStudentSubject) => {
                            if (selectedStudentSubject.subject) {
                                subjects.push(selectedStudentSubject.subject);
                            }
                            if (selectedStudentSubject['user']) {
                                users.push(selectedStudentSubject['user']);
                            }
                        });
                        const payload = {
                            users,
                            subjects,
                        };
                        new UserSubjectModel()
                            .bulkUpdateClearStudentStatuses(payload)
                            .then((responses) => {
                            console.log("🚀 ~ response:", responses);
                            // this.handleStatusChanged();
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
    getTemplates(subjectId) {
        new Template()
            .subject(subjectId)
            .include('file')
            .all()
            .then((templates) => {
            this.templates = templates;
        });
    }
    isActiveSubject(subject) {
        return subject.status !== 'finished' && subject.status !== 'closed';
    }
    countActiveAreaSubjects(area) {
        let count = 0;
        if (!area.topics)
            return count;
        area.topics.forEach((topic) => {
            topic.subjects.forEach((subject) => {
                if (this.isActiveSubject(subject)) {
                    count += 1;
                }
            });
        });
        return count > 0 ? `(${count})` : '';
    }
    countActiveTopicSubjects(topic) {
        let count = 0;
        if (!topic.subjects)
            return count;
        topic.subjects.forEach((subject) => {
            if (this.isActiveSubject(subject)) {
                count += 1;
            }
        });
        return count > 0 ? `(${count})` : '';
    }
    onSubjectUpdated(payload) {
        if (this.currentUserDialog && this.currentUserDialog.subjects && this.currentUserDialog.subjects[payload.status] !== undefined) {
            this.currentUserDialog.subjects[payload.status] += 1;
            this.currentUserDialog.subjects[this.previousUserSubjectStatus] -= 1;
            this.tableItems = this.tableItems.map((item) => {
                if (this.currentUserDialog && item.user.id === this.currentUserDialog.user.id) {
                    item = this.currentUserDialog;
                }
                return item;
            });
        }
        if ('has_priority' in payload && this.subjectTarget) {
            this.subjectTarget.has_priority = payload.has_priority;
        }
        if ('description' in payload && this.subjectTarget) {
            this.subjectTarget.description = payload.description;
        }
    }
    onPreviousSubjectStatus(subjectStatus) {
        this.previousUserSubjectStatus = subjectStatus;
    }
    async getYears() {
        await new Year()
            .all()
            .then((years) => {
            this.years = years;
            if (this.years[this.years.length - 1]) {
                this.tableFilters.years = [
                    this.years[this.years.length - 1],
                ];
            }
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    async fetchStatusSubjectSummaries() {
        return new SubjectStatusSummary()
            .filter(this.studentFilters)
            .include('treatment_labels')
            .all()
            .then((summaries) => {
            return summaries.map((summary) => {
                return { ...summary, student_subjects: [] };
            });
        });
    }
    async fetchUserLevel(id, filters) {
        return await new UserLevel()
            .userPeek(id)
            .filter(filters)
            .first();
    }
    async updatePriority(userId, subjectId, priority) {
        return await new UserSubjectModel().user(userId).putSubject([
            {
                subject: subjectId,
                has_priority: priority,
            },
        ]);
    }
    async onPriorityChange(state) {
        const subjectsToBeMappedTo = [];
        this.isLoading = true;
        await Promise.all(this.selectedStudentSubjects.map(async (selectedStudentSubject) => {
            if (!selectedStudentSubject.id || !selectedStudentSubject.subject) {
                return;
            }
            const subjectToUpdate = {
                has_priority: state,
                subject: selectedStudentSubject.subject,
                user: selectedStudentSubject['user'],
            };
            subjectsToBeMappedTo.push(subjectToUpdate);
            await this.updatePriority(selectedStudentSubject['user'], selectedStudentSubject.subject, state);
        }));
        this.isLoading = false;
        this.tableItems.forEach((item) => {
            item.student_subjects.forEach((studentSubject) => {
                studentSubject.topics.forEach((topic) => {
                    topic.subjects.forEach((subject) => {
                        const matchingChanges = subjectsToBeMappedTo.filter((change) => change.user === subject.user && change.subject === subject.subject);
                        matchingChanges.forEach((matchingChange) => {
                            subject.has_priority = matchingChange.has_priority;
                        });
                    });
                });
            });
        });
    }
    async fetchUserPeriod(id, filters) {
        return new UserPeriod()
            .userPeek(id)
            .filter(filters)
            .first();
    }
    async setTableItems() {
        this.isLoading = true;
        const tableItems = await this.fetchStatusSubjectSummaries();
        this.selectedStudentSubjects = [];
        this.tableItems = tableItems.map(item => {
            item.id = generateUuid();
            return item;
        });
        this.isLoading = false;
    }
    // #endregion
    // #region Getters & Setters
    get exclude() {
        return excludeRoles[this.$store.state.version].curriculum.subject.includes(this.$store.state.Auth.roles[0].id);
    }
    get snackbarActions() {
        const areSelectedSubjectsBusy = this.selectedStudentSubjects.every((subject) => subject.status === UserSubjectStatusEnum.HIGHLIGHTED);
        const containsInactiveSubject = this.selectedStudentSubjects.some((subject) => subject.status === UserSubjectStatusEnum.INACTIVE);
        const areSelectedUserSubjectsInSameSubject = this.selectedStudentSubjects.every((subject) => subject.subject === this.selectedStudentSubjects[0].subject);
        return [
            {
                icon: 'do_not_disturb_on',
                click: () => {
                    this.emptySelected();
                },
                label: this.$t('snackbar.actions.deselect').toString(),
            },
            ...(areSelectedSubjectsBusy
                ? [
                    {
                        icon: 'edit',
                        click: () => {
                            this.isDisplayingProgressDialog = true;
                        },
                        label: 'Voortgang',
                    },
                ]
                : []),
            ...(areSelectedUserSubjectsInSameSubject && !containsInactiveSubject
                ? [
                    {
                        icon: 'list',
                        click: () => {
                            this.isDisplayingBulkUpdateDescriptionDialog = true;
                        },
                        label: this.$t('snackbar.actions.roadmap').toString(),
                    },
                ]
                : []),
            {
                icon: 'nearby_error',
                click: () => {
                    this.onPriorityChange(true);
                },
                label: 'Prio geven',
            },
            {
                icon: 'not_interested',
                click: () => {
                    this.onPriorityChange(false);
                },
                label: 'Prio verwijderen',
            },
            ...(this.exclude
                ? []
                : [
                    {
                        icon: 'question_answer',
                        click: () => {
                            this.isDisplayingIdpNotesDialog = true;
                        },
                        label: this.$t('snackbar.actions.chat').toString(),
                    },
                    {
                        icon: 'error_outline',
                        click: () => {
                            this.$store.dispatch('openDialog', this.dialogOptionsResetStudentStatuses);
                        },
                        label: this.$t('snackbar.actions.reset_statuses').toString(),
                    },
                ]),
        ];
    }
    get goalSelectOptions() {
        if (this.group && this.group.projects && this.group.projects.length > 0) {
            return [
                { value: false, label: this.$tc('goalsetSelect.showAll') },
                { value: true, label: this.$tc('goalsetSelect.showGoalsetOnly') },
            ];
        }
        return [
            { value: false, label: this.$tc('goalsetSelect.showAll') },
        ];
    }
    get tableHeaders() {
        const filters = [
            {
                text: ' ',
                value: 'avatar',
                width: '10%',
                visible: true,
            },
            {
                text: this.$tc('name.title'),
                value: 'name',
                visible: true,
            },
            {
                text: '',
                visible: true,
            },
            {
                text: this.$tc('status'),
                value: 'status',
                width: '40%',
                visible: true,
            },
            {
                text: '  ',
                value: 'info',
                visible: this.hasAreasAndTopicsFilter,
            },
            {
                text: '',
                value: 'actions',
                width: '10%',
                visible: true,
            },
        ];
        return filters.filter((filter) => filter.visible);
    }
    get studentProfileLevels() {
        if (this.group.users) {
            const levels = uniq(this.group.users.map((user) => user.level.value).filter((item) => item).sort((a, b) => { return a - b; }));
            return levels.map((level) => {
                return {
                    value: level,
                    text: `${this.$tc('grade')} ${level}`,
                };
            });
        }
        else {
            return [];
        }
    }
    get hasAreasAndTopicsFilter() {
        return !!(this.tableFilters.area && this.tableFilters.topic);
    }
    get totalTableItems() {
        return this.students[0] && this.students[0].meta && this.students[0].meta.total
            ? this.students[0].meta.total
            : undefined;
    }
    get isLoadingStudentInfo() {
        return !!this.studentBeingLoaded;
    }
    get studentFilters() {
        const starts_at = this.tableFilters.years.length ? this.tableFilters.years.map((year) => year.starts_at).sort().shift() : '';
        const ends_at = this.tableFilters.years.length ? this.tableFilters.years.map((year) => year.ends_at).sort().pop() : '';
        const filters = {
            levels: this.tableFilters.levels.value || [],
            periods: this.tableFilters.periods.value || [],
            subject_labels: this.tableFilters.subject_labels.value || [],
            ...starts_at && ends_at ? { updated_at: [starts_at, ends_at] } : {},
            topic: this.tableFilters.topic,
            group: this.$route.params.id,
            student_profile_levels: this.tableFilters.student_profile_levels,
        };
        if (this.isFilteredOnGoalset) {
            filters.projects = this.group.projects.map((project) => project.id);
        }
        if (!this.tableFilters.topic) {
            filters.area = this.tableFilters.area;
        }
        return filters;
    }
    get isTopicSelectDisabled() {
        return !this.tableFilters.area.length;
    }
    get topicFilters() {
        return {
            parents: [this.tableFilters.area],
        };
    }
    get icons() {
        const type = Storage.get('type');
        return type && type.toLowerCase() === 'po' ? whiteAreaIcons : whiteKidIcons;
    }
    topicRowStyles(area) {
        const color = area.color || '#000';
        const lightColor = hex2rgba(color, 0.3);
        return {
            backgroundColor: `${lightColor}`,
        };
    }
    get userSubjectRooms() {
        return uniq(this.selectedStudentSubjects
            .filter((selectedUserSubject) => selectedUserSubject.status === 'highlighted' && selectedUserSubject.chat.default)
            .map((selectedUserSubject) => selectedUserSubject.chat.default));
    }
    get canSeeGreenTraficLight() {
        return !!this.$store.state.Auth.hasRole([RolesEnum.ADMIN, RolesEnum.TEACHER, RolesEnum.ROOT, RolesEnum.RESELLER]);
    }
    // #endregion
    // #region @Watchers
    updateTableData() {
        this.debouncedSetTableItems();
    }
    goalsetFilterChanger() {
        this.debouncedSetTableItems();
    }
};
__decorate([
    Watch('studentFilters', { immediate: true, deep: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudentOverview.prototype, "updateTableData", null);
__decorate([
    Watch('isFilteredOnGoalset', {}),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudentOverview.prototype, "goalsetFilterChanger", null);
StudentOverview = __decorate([
    Component({
        components: {
            ProgressDialog,
            SelectFilter,
            AreaSelect,
            TopicSelect,
            GoalsetSelect,
            StudentSubjectStatusSummary,
            TreatmentDialog,
            ProgressSelect,
            TrafficLight,
            EditSubjectDialog,
            ScoolSnackbar,
            BulkUpdateDescriptionDialog,
            UserSubjectMessageForm,
        },
    })
], StudentOverview);
export default StudentOverview;
